import { Box, Typography, Grid } from "@mui/material";
import dayjs from "dayjs";

import InfoField from "./infoField";

import type { Suppliers as SuppliersPayload } from "../../../utils/types/call";

interface SuppliersProps {
  suppliers: SuppliersPayload | null | undefined;
}

interface Field {
  label: string;
  value: string | null;
}

interface SectionItem {
  title: string;
  subtitle: string;
  fields: Field[][];
}

interface SectionConfig<T> {
  title: string;
  data: T[] | null | undefined;
  mapToItems: (item: T) => Omit<SectionItem, "title">;
}

const GridRow = ({ children }: { children: React.ReactNode }) => (
  <Grid container item spacing={1.5}>
    {children}
  </Grid>
);

const formatDate = (date: string | null | undefined): string | null =>
  date ? dayjs(date).format("MM/DD/YYYY") : null;

const Section = ({ title, items }: { title: string; items: SectionItem[] }) => (
  <Box mb={2}>
    <Typography
      variant="subtitle1"
      fontWeight={600}
      mb={1}
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
    >
      {title}
    </Typography>
    {items.map((item, index) => (
      <Box
        key={index}
        mb={1}
        p={1}
        borderRadius={1}
        sx={{
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="subtitle2"
          mb={0.5}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {item.subtitle}
        </Typography>
        <Grid container spacing={1} mb={1}>
          {item.fields.map((row, rowIndex) => (
            <GridRow key={rowIndex}>
              {row.map((field, fieldIndex) => (
                <Grid item xs={6} key={fieldIndex}>
                  <InfoField label={field.label} value={field.value} />
                </Grid>
              ))}
            </GridRow>
          ))}
        </Grid>
      </Box>
    ))}
  </Box>
);

const Suppliers = ({ suppliers }: SuppliersProps) => {
  const sections: SectionConfig<any>[] = [
    // {
    //   title: "Deliveries",
    //   data: suppliers?.deliveries,
    //   mapToItems: (delivery) => ({
    //     subtitle: `${delivery.deliveryType} - ${delivery.vendor}`,
    //     fields: [
    //       [
    //         { label: "Dock Location", value: delivery.dockLocation },
    //         {
    //           label: "Pallet Count",
    //           value: delivery.palletCount?.toString() ?? null,
    //         },
    //       ],
    //       [
    //         { label: "Delivery Date", value: formatDate(delivery.deliveryDate) },
    //         { label: "Launch", value: delivery.launch },
    //       ],
    //     ],
    //   }),
    // },
    {
      title: "Bunkers / Fresh Water",
      // data: suppliers?.bunkers,
      // mapToItems: (bunker) => ({
      //   subtitle: `${bunker.type} - ${bunker.supplier}`,
      //   fields: [
      //     [
      //       { label: "Dock Location", value: bunker.dockLocation },
      //       { label: "MTS", value: bunker.mts?.toString() ?? null },
      //     ],
      //     [
      //       { label: "Delivery Date", value: formatDate(bunker.deliveryDate) },
      //       { label: "Pallet Count", value: bunker.palletCount },
      //     ],
      //   ],
      // }),
      data: suppliers?.bunkers,
      mapToItems: (bunker) => ({
        subtitle:
          !bunker.type && !bunker.supplier
            ? "Unknown"
            : `${bunker.type || "Unknown"} - ${bunker.supplier || "Unknown"}`,
        fields: [
          [
            { label: "Berth", value: bunker.berth || "Unknown" },
            { label: "MTS", value: bunker.mts?.toString() ?? null },
          ],
          [
            {
              label: "Delivery Date",
              value: formatDate(bunker.delivery_date) || "Unknown",
            },
            { label: "Oil Type", value: bunker.oil_type || "Unknown" },
          ],
        ],
      }),
    },
    // {
    //   title: "Onboard Services",
    //   data: suppliers?.onboardServices,
    //   mapToItems: (service) => ({
    //     subtitle: `${service.typeOfService} - ${service.vendor}`,
    //     fields: [
    //       [
    //         { label: "Dock Location", value: service.dockLocation },
    //         { label: "Date Completed", value: formatDate(service.dateCompleted) },
    //       ],
    //     ],
    //   }),
    // },
  ];

  const hasData = sections.some((section) => (section.data?.length ?? 0) > 0);

  return (
    <Box>
      {sections.map(({ title, data, mapToItems }) =>
        data?.length ? (
          <Section
            key={title}
            title={title}
            items={data.map((item) => ({ title, ...mapToItems(item) }))}
          />
        ) : null
      )}

      {!hasData && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No suppliers found
        </Typography>
      )}
    </Box>
  );
};

export default Suppliers;
